import {
  LabelSelector,
  loadBalancerPrice,
  type LoadBalancerAlgorithm,
  type LoadBalancerTargetInput,
} from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCertificates, useCreateLoadBalancer } from "../api/load-balancers.js";
import { useNetworks } from "../api/networks.js";
import { useCatalogue, useProject } from "../api/queries.js";
import { useServers } from "../api/servers.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { money } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { PageHeader } from "../ui/layout.js";
import { Alert, Button, Card, TextArea, TextField } from "../ui/primitives.js";
import { useToast } from "../ui/toast.js";
import { selectorsOf } from "./firewall-new.js";
import { parseLabels } from "./labels.js";
import {
  draftOf,
  draftValid,
  FieldProblems,
  inputOf,
  ServiceFields,
  type ServiceDraft,
} from "./load-balancer-service.js";
import { SelectField } from "./networks.js";

function Step({
  n,
  title,
  lead,
  children,
}: {
  n: number;
  title: string;
  lead?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <section className="flex flex-col gap-3" aria-labelledby={`lb-step-${n}`}>
        <div className="flex items-start gap-3">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent-strong">
            {n}
          </span>
          <div className="flex flex-col gap-0.5">
            <h2 id={`lb-step-${n}`} className="text-base font-semibold">
              {title}
            </h2>
            {lead && <p className="text-sm text-muted">{lead}</p>}
          </div>
        </div>
        {children}
      </section>
    </Card>
  );
}

const DEFAULT_REGION = "fsn1";

export function LoadBalancerCreatePage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const project = useProject(projectId);
  const regions = useCatalogue(projectId, "regions").data ?? [];
  const types = useCatalogue(projectId, "load-balancer-types").data ?? [];
  const prices = useCatalogue(projectId, "prices").data;
  const networks = useNetworks(projectId).data ?? [];
  const servers = useServers(projectId).data ?? [];
  const certificates = useCertificates(projectId).data ?? [];
  const create = useCreateLoadBalancer(projectId);
  const readOnly = project.data?.connection?.permission === "read";

  const [region, setRegion] = useState("");
  const [type, setType] = useState("lb11");
  const [algorithm, setAlgorithm] = useState<LoadBalancerAlgorithm>("round_robin");
  const [publicInterface, setPublicInterface] = useState(true);
  const [networkId, setNetworkId] = useState("");
  const [withService, setWithService] = useState(true);
  const [service, setService] = useState<ServiceDraft>(() => draftOf());
  const [serverIds, setServerIds] = useState<Set<string>>(new Set());
  const [usePrivateIp, setUsePrivateIp] = useState(false);
  const [selectors, setSelectors] = useState("");
  const [name, setName] = useState("");
  const [labelsText, setLabelsText] = useState("");
  const [tried, setTried] = useState(false);

  const chosenRegion =
    region ||
    (regions.some((r) => r.code === DEFAULT_REGION) ? DEFAULT_REGION : regions[0]?.code) ||
    "";
  const zone = regions.find((r) => r.code === chosenRegion)?.zone ?? null;
  const zones = [...new Set(regions.map((r) => r.zone))];
  const zoneNetworks = networks.filter((n) =>
    n.subnets.some((s) => s.type === "cloud" && (zone === null || s.zone === zone)),
  );
  const network = zoneNetworks.find((n) => n.id === networkId) ?? null;
  const price = loadBalancerPrice({ type, region: chosenRegion }, types);
  const currency = prices?.currency ?? "EUR";
  const fmt = (amount: string | undefined, digits = 2) =>
    amount === undefined ? "—" : money(amount, currency, i18n.language, digits);
  const selectorList = selectorsOf(selectors);
  const badSelector = selectorList.find((s) => !LabelSelector.safeParse(s).success);
  const labels = parseLabels(labelsText);
  const privateOk = network !== null;
  const invalid =
    !name.trim() ||
    labels.labels === null ||
    badSelector !== undefined ||
    (withService && !draftValid(service)) ||
    (!publicInterface && !network) ||
    !chosenRegion;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (invalid || !labels.labels) return;
    const targets: LoadBalancerTargetInput[] = [
      ...[...serverIds].map((serverId) => ({
        type: "server" as const,
        serverId,
        usePrivateIp: privateOk && usePrivateIp,
      })),
      ...selectorList.map((selector) => ({
        type: "label_selector" as const,
        selector,
        usePrivateIp: privateOk && usePrivateIp,
      })),
    ];
    create.mutate(
      {
        name: name.trim(),
        type,
        region: chosenRegion,
        algorithm,
        publicInterface,
        ...(network ? { networkId: network.id } : {}),
        services: withService ? [inputOf(service)] : [],
        targets,
        labels: labels.labels,
      },
      {
        onSuccess: ({ loadBalancer }) => {
          toast({ tone: "ok", title: t("loadBalancers.created", { name: loadBalancer.name }) });
          void navigate({
            to: "/projects/$projectId/load-balancers/$loadBalancerId",
            params: { projectId, loadBalancerId: loadBalancer.id },
          });
        },
      },
    );
  };

  return (
    <>
      <Link
        to="/projects/$projectId/load-balancers"
        params={{ projectId }}
        className="inline-flex min-h-6 items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t("loadBalancers.back")}
      </Link>
      <PageHeader
        title={t("loadBalancers.newTitle")}
        intro={t("loadBalancers.newIntro")}
        help="load-balancers"
      />
      {readOnly && <Alert tone="warn">{t("errors.reasons.read_only_token")}</Alert>}
      <form
        onSubmit={submit}
        noValidate
        className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start"
      >
        <div className="flex min-w-0 flex-col gap-4">
          <Step
            n={1}
            title={t("loadBalancers.stepLocation")}
            lead={t("loadBalancers.stepLocationLead")}
          >
            <SelectField
              label={t("loadBalancers.location")}
              name="region"
              value={chosenRegion}
              onChange={(code) => {
                setRegion(code);
                setNetworkId("");
              }}
            >
              {zones.map((z) => (
                <optgroup key={z} label={t("loadBalancers.zone", { zone: z })}>
                  {regions
                    .filter((r) => r.zone === z)
                    .map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.city} ({r.code})
                      </option>
                    ))}
                </optgroup>
              ))}
            </SelectField>
          </Step>
          <Step n={2} title={t("loadBalancers.stepType")} lead={t("loadBalancers.stepTypeLead")}>
            {types.length === 0 ? (
              <Alert tone="warn">{t("errors.reasons.catalogue_not_synced")}</Alert>
            ) : (
              <fieldset className="grid gap-2 sm:grid-cols-3">
                <legend className="sr-only">{t("loadBalancers.type")}</legend>
                {types.map((lbType) => {
                  const offered = loadBalancerPrice(
                    { type: lbType.code, region: chosenRegion },
                    types,
                  );
                  return (
                    <label
                      key={lbType.code}
                      className="flex cursor-pointer flex-col gap-1 rounded-lg border border-line px-3 py-2 text-sm has-[:checked]:border-accent has-[:disabled]:opacity-60"
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="type"
                          checked={type === lbType.code}
                          disabled={!offered}
                          onChange={() => setType(lbType.code)}
                          className="size-4 accent-accent"
                        />
                        <span className="font-semibold">{lbType.code.toUpperCase()}</span>
                      </span>
                      <span className="text-xs text-muted">
                        {t("loadBalancers.typeLimits", {
                          services: lbType.maxServices,
                          targets: lbType.maxTargets,
                          certificates: lbType.maxCertificates,
                        })}
                      </span>
                      <span className="text-xs text-muted">
                        {t("loadBalancers.typeConnections", {
                          count: lbType.maxConnections,
                        })}
                      </span>
                      <span className="font-medium">
                        {offered
                          ? t("loadBalancers.pricePair", {
                              hourly: fmt(offered.hourly.net, 4),
                              monthly: fmt(offered.monthly.net),
                            })
                          : t("loadBalancers.notOffered")}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            )}
            <p className="text-xs text-muted">{t("loadBalancers.billing")}</p>
          </Step>
          <Step
            n={3}
            title={t("loadBalancers.stepNetwork")}
            lead={t("loadBalancers.stepNetworkLead")}
          >
            <label className="flex min-h-6 items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-accent"
                checked={publicInterface}
                onChange={(e) => setPublicInterface(e.target.checked)}
              />
              <span className="flex flex-col">
                <span className="font-medium">{t("loadBalancers.publicInterface")}</span>
                <span className="text-xs text-muted">{t("loadBalancers.publicInterfaceHint")}</span>
              </span>
            </label>
            <SelectField
              label={t("loadBalancers.network")}
              name="network"
              value={networkId}
              onChange={setNetworkId}
              hint={t("loadBalancers.networkHint")}
            >
              <option value="">{t("loadBalancers.noNetwork")}</option>
              {zoneNetworks.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name} ({n.ipRange})
                </option>
              ))}
            </SelectField>
            {tried && !publicInterface && !network && (
              <Alert tone="danger">{t("errors.reasons.lb_needs_network")}</Alert>
            )}
          </Step>
          <Step n={4} title={t("loadBalancers.stepAlgorithm")}>
            <fieldset className="flex flex-col gap-2 text-sm">
              <legend className="sr-only">{t("loadBalancers.algorithm")}</legend>
              {(["round_robin", "least_connections"] as const).map((a) => (
                <label key={a} className="flex min-h-6 items-start gap-2">
                  <input
                    type="radio"
                    name="algorithm"
                    checked={algorithm === a}
                    onChange={() => setAlgorithm(a)}
                    className="mt-0.5 size-4 accent-accent"
                  />
                  <span className="flex flex-col">
                    <span className="font-medium">{t(`loadBalancers.algorithms.${a}`)}</span>
                    <span className="text-xs text-muted">
                      {t(`loadBalancers.algorithmHints.${a}`)}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          </Step>
          <Step
            n={5}
            title={t("loadBalancers.stepService")}
            lead={t("loadBalancers.stepServiceLead")}
          >
            <label className="flex min-h-6 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-accent"
                checked={withService}
                onChange={(e) => setWithService(e.target.checked)}
              />
              {t("loadBalancers.withService")}
            </label>
            {withService && (
              <ServiceFields
                projectId={projectId}
                draft={service}
                onChange={setService}
                showErrors={tried}
                certificates={certificates}
              />
            )}
          </Step>
          <Step
            n={6}
            title={t("loadBalancers.stepTargets")}
            lead={t("loadBalancers.stepTargetsLead")}
          >
            {servers.length === 0 ? (
              <p className="text-sm text-muted">{t("loadBalancers.noServersInProject")}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
                {servers.map((s) => {
                  const otherZone = zone !== null && s.zone !== zone;
                  return (
                    <li key={s.id}>
                      <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          className="size-4 accent-accent"
                          disabled={otherZone}
                          checked={serverIds.has(s.id)}
                          onChange={() => {
                            const next = new Set(serverIds);
                            if (next.has(s.id)) next.delete(s.id);
                            else next.add(s.id);
                            setServerIds(next);
                          }}
                        />
                        <span className="min-w-0 flex-1 truncate font-medium" title={s.name}>
                          {s.name}
                        </span>
                        <span className="text-xs text-muted">
                          {otherZone ? t("loadBalancers.why.otherZone") : s.region}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <TextArea
              label={t("loadBalancers.labelSelectors")}
              name="labelSelectors"
              value={selectors}
              onChange={(e) => setSelectors(e.target.value)}
              rows={2}
              spellCheck={false}
              placeholder="role=web"
              hint={t("loadBalancers.labelSelectorsHint")}
              error={badSelector ? t("firewalls.selectorInvalid") : undefined}
            />
            <label className="flex min-h-6 items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-accent"
                disabled={!privateOk}
                checked={privateOk && usePrivateIp}
                onChange={(e) => setUsePrivateIp(e.target.checked)}
              />
              <span className="flex flex-col">
                <span className="font-medium">{t("loadBalancers.usePrivateIp")}</span>
                <span className="text-xs text-muted">
                  {privateOk
                    ? t("loadBalancers.usePrivateIpHint")
                    : t("loadBalancers.usePrivateIpNeedsNetwork")}
                </span>
              </span>
            </label>
          </Step>
          <Step n={7} title={t("loadBalancers.stepName")}>
            <TextField
              label={t("loadBalancers.name")}
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={128}
              hint={t("loadBalancers.nameHint")}
              error={
                tried && !name.trim()
                  ? t("errors.fieldInvalid")
                  : fieldError(create.error, "name", t)
              }
            />
            <TextArea
              label={t("networks.labels")}
              name="labels"
              value={labelsText}
              onChange={(e) => setLabelsText(e.target.value)}
              rows={2}
              spellCheck={false}
              placeholder="env=production"
              hint={t("networks.labelsHint")}
              error={
                labels.badLine !== null
                  ? t("networks.labelsInvalid", { line: labels.badLine })
                  : undefined
              }
            />
          </Step>
        </div>
        <aside className="lg:sticky lg:top-20">
          <Card>
            <div className="flex flex-col gap-3 text-sm">
              <h2 className="text-base font-semibold">{t("loadBalancers.summary")}</h2>
              <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
                <dt className="text-muted">{t("loadBalancers.type")}</dt>
                <dd>{type.toUpperCase()}</dd>
                <dt className="text-muted">{t("loadBalancers.location")}</dt>
                <dd>{chosenRegion || "—"}</dd>
                <dt className="text-muted">{t("loadBalancers.services")}</dt>
                <dd>{withService ? `${service.listenPort} → ${service.destinationPort}` : "—"}</dd>
                <dt className="text-muted">{t("loadBalancers.targets")}</dt>
                <dd>{serverIds.size + selectorList.length}</dd>
                <dt className="flex items-center gap-1 text-muted">
                  {t("loadBalancers.pricePerHour")}
                  <FieldHint text={t("loadBalancers.billing")} topic="load-balancers" />
                </dt>
                <dd className="tabular-nums">{fmt(price?.hourly.net, 4)}</dd>
                <dt className="text-muted">{t("loadBalancers.pricePerMonth")}</dt>
                <dd className="tabular-nums">{fmt(price?.monthly.net)}</dd>
              </dl>
              {tried && invalid && <Alert tone="danger">{t("loadBalancers.fixErrors")}</Alert>}
              <FieldProblems error={create.error} />
              <ErrorMessage error={create.error} />
              <Button type="submit" busy={create.isPending} disabled={readOnly}>
                {t("loadBalancers.submit")}
              </Button>
            </div>
          </Card>
        </aside>
      </form>
    </>
  );
}
